import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TablePagination,
  TableRow,
  Typography
} from '@mui/material'
import useRidesQuery from '../../../hooks/useRidesQuery'
import moment from 'moment-timezone'
import { etaMinToTime } from '../../../utils/utils-functions'
import AccessibleIcon from '@mui/icons-material/Accessible'
import RideIcon from '../../../components/ui/RideIcon'
import useRidesStore from '../../../stores/rides'
import useRideByIdQuery from '../../../hooks/useRideByIdQuery'
import { ITableRide } from '../../../services/create-ride/api'
import { memo, useMemo } from 'react'
import { ridesTypesWavIds } from '../ridesConfig'

type TableRowProps = {
  ride: ITableRide
  isSelected: boolean
}

const getAddressPart = (address: string, part: number) => {
  const splitAddress = address.split(',')
  return splitAddress[part] ? splitAddress[part] : splitAddress[1] ?? ''
}

const Row = memo(function RideRow({ ride, isSelected }: TableRowProps) {
  const setSelectedRideId = useRidesStore(state => state.setSelectedRideId)

  const showPickupAddress = useMemo(() => getAddressPart(ride.start_address, 0), [
    ride.start_address
  ])

  const showDropoffAddress = useMemo(() => getAddressPart(ride.destination_address, 1), [
    ride.destination_address
  ])

  return (
    <TableRow
      key={ride.id}
      onClick={() => setSelectedRideId(ride.id)}
      className={`ride-row__${ride.status} ${isSelected ? 'selected' : ''}`}>
      <TableCell>
        <Typography variant="subtitle2">{ride.mta_ref_id}</Typography>
      </TableCell>
      <TableCell>
        <Typography variant="subtitle2">{ride.first_name}</Typography>
      </TableCell>
      <TableCell>
        <Typography variant="subtitle1">{ride.driver_first_name}</Typography>
        <Typography variant="subtitle1">{ride.driver_last_name}</Typography>
      </TableCell>
      <TableCell className="address">
        <Typography variant="subtitle1">{showPickupAddress}</Typography>
        <Typography variant="subtitle2">{moment(ride.pickup_at).format('hh:mm A')}</Typography>
      </TableCell>
      <TableCell className="address">
        <Typography variant="subtitle1">{showDropoffAddress}</Typography>
        <Typography variant="subtitle2">{moment(ride.dropoff_at).format('hh:mm A')}</Typography>
      </TableCell>
      <TableCell>
        {etaMinToTime(ride.eta)}
      </TableCell>
      <TableCell align="right" className="icons" >
        {ridesTypesWavIds.includes(ride.ride_type_id) ? <AccessibleIcon /> : null}
        <RideIcon status={ride.status} />
      </TableCell>
    </TableRow>
  )
})

const Pagination = memo(function TableRidesPagination() {
  const page = useRidesStore(state => state.searchOptions.page)
  const perPage = useRidesStore(state => state.searchOptions.per_page)
  const updateSearchOptions = useRidesStore(state => state.updateSearchOptions)

  return (
    <TablePagination
      component="div"
      rowsPerPageOptions={[5, 10, 25, 50, 75, 100, 150, 200, 500, 1000, 2000, 5000]}
      count={1000}
      rowsPerPage={perPage}
      page={page}
      SelectProps={{
        inputProps: {
          'aria-label': 'rows per page'
        }
      }}
      onPageChange={(e, newPage) => updateSearchOptions({ page: newPage })}
      onRowsPerPageChange={e => updateSearchOptions({ per_page: parseInt(e.target.value, 10) })}
    />
  )
})

const RidesTable = () => {
  const { data } = useRidesQuery()
  const { data: rideData } = useRideByIdQuery()

  const rides = data?.rides.data || []

  return (
    <div className="table-wrapper">
      <TableContainer component="div">
        <Table className="rides-table" aria-label="custom pagination table">
          <TableBody>
            {rides.map(ride => (
              <Row key={ride.id} ride={ride} isSelected={rideData?.ride.id === ride.id} />
            ))}
          </TableBody>
        </Table>
      </TableContainer>
      <Pagination />
    </div>
  )
}

export default RidesTable

